import { useCallback, useEffect, useRef, useState } from 'react';
import { request } from '../api/client';

const ENDPOINTS = {
  admin: '/api/admin/payment-transactions/customer-email',
  cp: '/api/cp/payment/manual/customer-email',
  terminal: '/api/terminal/payment/manual/customer-email',
};

function validTransactionId(value) {
  const text = String(value ?? '');
  return /^\d+$/.test(text) && Number.isSafeInteger(Number(text)) && Number(text) > 0;
}

/** Fetch the current GASYSTEM customer email for the selected transaction. */
export default function useCurrentReceiptEmail({ role, open, transactionId, setEmail, enabled }) {
  const [status, setStatus] = useState('idle');
  const [retryCount, setRetryCount] = useState(0);
  const callbackRef = useRef(setEmail);
  callbackRef.current = setEmail;
  const dirtyRef = useRef(false);
  const sessionRef = useRef(null);
  const sequenceRef = useRef(0);
  const sessionKey = enabled && open ? `${role}:${String(transactionId ?? '')}` : null;
  const renderedSessionRef = useRef(sessionKey);
  renderedSessionRef.current = sessionKey;

  const markEdited = useCallback(() => { dirtyRef.current = true; }, []);
  const retry = useCallback(() => { setRetryCount((count) => count + 1); }, []);

  useEffect(() => {
    const sequence = ++sequenceRef.current;
    if (!sessionKey) {
      sessionRef.current = null;
      dirtyRef.current = false;
      setStatus('idle');
      return undefined;
    }

    if (sessionRef.current !== sessionKey) {
      sessionRef.current = sessionKey;
      dirtyRef.current = false;
      callbackRef.current('');
    }
    if (!ENDPOINTS[role] || !validTransactionId(transactionId)) {
      setStatus('error');
      return undefined;
    }

    const controller = new AbortController();
    setStatus('loading');
    request({
      method: 'GET', url: ENDPOINTS[role], params: { id: transactionId },
      timeout: 30000, signal: controller.signal,
    }).then((data) => {
      if (controller.signal.aborted || sequence !== sequenceRef.current || renderedSessionRef.current !== sessionKey) return;
      if (String(data?.transactionId ?? '') !== String(transactionId)) {
        setStatus('error');
        return;
      }
      if (data.emailStatus === 'AVAILABLE' && typeof data.currentCustomerEmail === 'string' && data.currentCustomerEmail.trim()) {
        if (!dirtyRef.current) callbackRef.current(data.currentCustomerEmail.trim());
        setStatus('available');
      } else if (data.emailStatus === 'EMPTY') {
        setStatus('empty');
      } else if (data.emailStatus === 'INVALID') {
        setStatus('invalid');
      } else {
        setStatus('error');
      }
    }).catch(() => {
      if (!controller.signal.aborted && sequence === sequenceRef.current && renderedSessionRef.current === sessionKey) setStatus('error');
    });
    return () => controller.abort();
  }, [enabled, open, role, transactionId, sessionKey, retryCount]);

  return { status, retry, markEdited };
}
